/**
 * Projected per-license returns at current pool occupancy, used by the lease
 * duration picker to price each term.
 *
 * Plain hook with a manual `refetch`: the numbers move only when someone else
 * leases, so there is no polling — the lease form refetches after a successful
 * creation.
 */
import { logger } from "@swarm/shared/logger";
import type { LeaseNetworkStatsResponse } from "@swarm/shared/schemas";
import { useEffect, useState } from "react";

import { apiErrorMessage } from "@/lib/api/client";
import { getLeaseNetworkStats } from "@/lib/api/license-leases";

interface UseNetworkStatsResult {
  networkStats: LeaseNetworkStatsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useNetworkStats = (): UseNetworkStatsResult => {
  const [networkStats, setNetworkStats] = useState<LeaseNetworkStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNetworkStats = async () => {
    try {
      setIsLoading(true);
      setError(null);

      setNetworkStats(await getLeaseNetworkStats());
    } catch (err) {
      logger.error(
        { err: err instanceof Error ? { message: err.message } : err },
        "failed to fetch network stats",
      );
      setError(apiErrorMessage(err, "Failed to fetch network stats"));
      setNetworkStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNetworkStats();
  }, []);

  return {
    networkStats,
    isLoading,
    error,
    refetch: fetchNetworkStats,
  };
};
